import { QUERY, type QueryOperation } from 'api/Query';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { TgaCoverageSources } from 'ts/base/routing/search/schema/TgaCoverageSources';
import type { SortOptions } from 'ts/base/table/SortableTable';
import { Links } from 'ts/commons/links/Links';
import { EIssueLikeQueryableEntityTypeField } from 'ts/commons/query/EIssueLikeQueryableEntityTypeField';
import type { AutocompletionResult } from 'typedefs/AutocompletionResult';
import type { EKeyedObjectTypeEntry } from 'typedefs/EKeyedObjectType';
import { EProjectPermission } from 'typedefs/EProjectPermission';
import { EType } from 'typedefs/EType';
import type { IssueQueryResult } from 'typedefs/IssueQueryResult';
import type { StoredQueryDescriptor } from 'typedefs/StoredQueryDescriptor';
import type { TeamscaleIssueId } from 'typedefs/TeamscaleIssueId';

/** An article for a word. */
type Article = 'a' | 'an';

/** Base type for dealing with queryable objects in Teamscale like issues, spec items or tests. */
export abstract class QueryableEntityType {
	/** The lower-cased version of the entity's name. */
	public readonly lowerCasedName: string;

	protected constructor(
		public readonly article: Article,
		public readonly capitalizedName: string,
		public readonly requiredProjectPermission: EProjectPermission,
		public readonly pathPrefixType: EType,
		public readonly sampleQuery: string,
		public readonly sampleQueryName: string
	) {
		this.lowerCasedName = capitalizedName.toLowerCase();
	}

	/** Returns a query operation for autocomplete suggestions for the given token. */
	public abstract autocomplete(projectId: string, token: string): QueryOperation<AutocompletionResult>;

	/** Builds a link to the given metric descriptor. */
	public abstract getStoredQueryLink(projectId: string, metricDescriptor: StoredQueryDescriptor): string;

	/** Retrieves all available metric descriptors for the entity type. */
	public abstract getStoredQueryDescriptors(projectId: string): QueryOperation<StoredQueryDescriptor[]>;

	/** Creates a new metric based on the given metric descriptor. */
	public abstract createMetric(projectId: string, metricDescriptor: StoredQueryDescriptor): Promise<void>;

	/** Deletes a metric with the given name. */
	public abstract deleteStoredQuery(projectId: string, metricName: string): Promise<void>;

	/** Validates the given query. */
	public abstract isQueryValid(projectId: string, query: string): QueryOperation<boolean>;
}

/**
 * Provides some additional functionalities that are only available for issues and spec items as they operate on
 * issue-like data.
 */
export abstract class IssueLikeQueryableEntityType extends QueryableEntityType {
	protected constructor(
		article: Article,
		capitalizedName: string,
		requiredProjectPermission: EProjectPermission,
		pathPrefix: EType,
		public readonly defaultColumns: string[],
		sampleQueryText: string,
		sampleQueryName: string
	) {
		super(article, capitalizedName, requiredProjectPermission, pathPrefix, sampleQueryText, sampleQueryName);
	}

	/** Returns the total number of available entities. */
	public abstract getOverallCount(projectId: string, commit: UnresolvedCommitDescriptor): QueryOperation<number>;

	/** Performs a query for the entity type. */
	public abstract performQuery(
		projectId: string,
		query: string,
		startIndex: number,
		maxResult?: number,
		sortOptions?: SortOptions,
		commit?: UnresolvedCommitDescriptor
	): Promise<IssueQueryResult>;

	/** Returns all known columns and their types. */
	public abstract getKnownColumns(
		projectId: string,
		commit: UnresolvedCommitDescriptor
	): QueryOperation<Record<string, EKeyedObjectTypeEntry>>;

	/** Returns a link to a concrete entity's detail view. */
	public abstract getEntityLink(
		projectId: string,
		issueId: TeamscaleIssueId,
		commit?: UnresolvedCommitDescriptor,
		selectedTestGapBranch?: string | null,
		tgaCoverageSources?: TgaCoverageSources
	): string;
}

/** Describes an issue as an entity. */
class IssueEntityDescription extends IssueLikeQueryableEntityType {
	public constructor() {
		super(
			'an',
			'Issue',
			EProjectPermission.EDIT_ISSUE_QUERIES,
			EType.ISSUE_QUERY,
			[
				EIssueLikeQueryableEntityTypeField.ID,
				EIssueLikeQueryableEntityTypeField.SUBJECT,
				EIssueLikeQueryableEntityTypeField.STATUS,
				EIssueLikeQueryableEntityTypeField.ASSIGNEE
			],
			'status = Open && Priority = Critical',
			'statistics/bugs/open'
		);
	}

	public override autocomplete(projectId: string, token: string): QueryOperation<AutocompletionResult> {
		return QUERY.getIssueQueryAutocompletionSuggestions(projectId, { token });
	}

	public override getOverallCount(projectId: string): QueryOperation<number> {
		return QUERY.getIssueCount(projectId);
	}

	public override getStoredQueryDescriptors(projectId: string): QueryOperation<StoredQueryDescriptor[]> {
		return QUERY.getIssueQueries(projectId);
	}

	public override createMetric(projectId: string, metricDescriptor: StoredQueryDescriptor): Promise<void> {
		return QUERY.saveIssueQueryDescriptor(projectId, metricDescriptor).fetch();
	}

	public override deleteStoredQuery(projectId: string, metricName: string): Promise<void> {
		return QUERY.deleteIssueQueryDescriptor(projectId, metricName).fetch();
	}

	public override isQueryValid(projectId: string, query: string): QueryOperation<boolean> {
		return QUERY.isIssueQueryValid(projectId, { query });
	}

	public override performQuery(
		projectId: string,
		query: string,
		startIndex: number,
		maxResult?: number,
		sortOptions?: SortOptions,
		commit?: UnresolvedCommitDescriptor
	): Promise<IssueQueryResult> {
		return QUERY.performIssueQuery(projectId, {
			query,
			start: startIndex,
			max: maxResult,
			'sort-by': sortOptions?.sortByField,
			'sort-order': sortOptions?.sortOrder.name,
			t: commit
		}).fetch();
	}

	public override getKnownColumns(
		projectId: string,
		commit: UnresolvedCommitDescriptor
	): QueryOperation<Record<string, EKeyedObjectTypeEntry>> {
		return QUERY.getKnownIssueQueryColumns(projectId, { t: commit });
	}

	public override getStoredQueryLink(projectId: string, storedQueryDescriptor: StoredQueryDescriptor): string {
		return Links.issueQuery(projectId, storedQueryDescriptor.query, storedQueryDescriptor.name);
	}

	public override getEntityLink(
		projectId: string,
		issueId: TeamscaleIssueId,
		commit?: UnresolvedCommitDescriptor,
		selectedTestGapBranch?: string | null,
		tgaCoverageSources?: TgaCoverageSources
	): string {
		return Links.issue(projectId, issueId, {
			selectedTestGapBranch: selectedTestGapBranch ?? commit?.branchName,
			tgaCoverageSources
		});
	}
}

/** Describes a spec item as an entity. */
class SpecItemEntityDescription extends IssueLikeQueryableEntityType {
	public constructor() {
		super(
			'a',
			'Specification Item',
			EProjectPermission.EDIT_SPEC_ITEM_QUERIES,
			EType.SPEC_ITEM_QUERY,
			[
				EIssueLikeQueryableEntityTypeField.ID,
				EIssueLikeQueryableEntityTypeField.SUBJECT,
				EIssueLikeQueryableEntityTypeField.STATUS,
				EIssueLikeQueryableEntityTypeField.ASSIGNEE
			],
			'status = Open && Priority = Critical',
			'Requirements in state draft'
		);
	}

	public override autocomplete(projectId: string, token: string): QueryOperation<AutocompletionResult> {
		return QUERY.getSpecItemQueryAutocompletionSuggestions(projectId, { token });
	}

	public override getOverallCount(projectId: string, commit: UnresolvedCommitDescriptor): QueryOperation<number> {
		return QUERY.getSpecItemCount(projectId, { t: commit });
	}

	public override getStoredQueryDescriptors(projectId: string): QueryOperation<StoredQueryDescriptor[]> {
		return QUERY.getSpecItemQueries(projectId);
	}

	public override createMetric(projectId: string, metricDescriptor: StoredQueryDescriptor): Promise<void> {
		return QUERY.saveSpecItemQueryDescriptor(projectId, metricDescriptor).fetch();
	}

	public override deleteStoredQuery(projectId: string, metricName: string): Promise<void> {
		return QUERY.deleteSpecItemQueryDescriptor(projectId, metricName).fetch();
	}

	public override isQueryValid(projectId: string, query: string): QueryOperation<boolean> {
		return QUERY.isSpecItemQueryValid(projectId, { query });
	}

	public override performQuery(
		projectId: string,
		query: string,
		startIndex: number,
		maxResult?: number,
		sortOptions?: SortOptions,
		commit?: UnresolvedCommitDescriptor
	): Promise<IssueQueryResult> {
		return QUERY.performSpecItemQuery(projectId, {
			t: commit,
			query,
			start: startIndex,
			max: maxResult,
			'sort-by': sortOptions?.sortByField,
			'sort-order': sortOptions?.sortOrder.name
		}).fetch();
	}

	public override getKnownColumns(
		projectId: string,
		commit: UnresolvedCommitDescriptor
	): QueryOperation<Record<string, EKeyedObjectTypeEntry>> {
		return QUERY.getKnownSpecItemColumns(projectId, { t: commit });
	}

	public override getStoredQueryLink(projectId: string, storedQueryDescriptor: StoredQueryDescriptor): string {
		return Links.specItemQuery(projectId, {
			query: storedQueryDescriptor.query,
			queryName: storedQueryDescriptor.name
		});
	}

	public override getEntityLink(
		projectId: string,
		issueId: TeamscaleIssueId,
		commit?: UnresolvedCommitDescriptor
	): string {
		return Links.specItem(projectId, issueId, { commit });
	}
}

/** Describes a test query as an entity. */
class TestEntityDescription extends QueryableEntityType {
	public constructor() {
		super(
			'a',
			'Test',
			EProjectPermission.EDIT_TEST_QUERIES,
			EType.TEST_QUERY,
			'result() in [failed, error]',
			'Failing tests'
		);
	}

	public override autocomplete(projectId: string, token: string): QueryOperation<AutocompletionResult> {
		return QUERY.getAutocompletionSuggestions(projectId, { token });
	}

	public override getStoredQueryDescriptors(projectId: string): QueryOperation<StoredQueryDescriptor[]> {
		return QUERY.getStoredTestQueries(projectId);
	}

	public override createMetric(projectId: string, metricDescriptor: StoredQueryDescriptor): Promise<void> {
		return QUERY.saveTestQueryDescriptor(projectId, metricDescriptor).fetch();
	}

	public override deleteStoredQuery(projectId: string, metricName: string): Promise<void> {
		return QUERY.deleteTestQueryDescriptor(projectId, metricName).fetch();
	}

	public override isQueryValid(projectId: string, query: string): QueryOperation<boolean> {
		return QUERY.isTestQueryValid(projectId, { query });
	}

	public override getStoredQueryLink(projectId: string, storedQueryDescriptor: StoredQueryDescriptor): string {
		return Links.testQuery(projectId, storedQueryDescriptor.query, { queryName: storedQueryDescriptor.name });
	}
}

/** Holds instances of all available queryable entities. */
export class QueryableEntities {
	/** Entity description for issues from an issue tracker. */
	public static readonly ISSUE = new IssueEntityDescription();
	/** Entity description for specification items from a requirements tracing connector. */
	public static readonly SPEC_ITEM = new SpecItemEntityDescription();
	/**
	 * Entity description for tests that originate either from tests detected in the source code or from an external
	 * test execution upload.
	 */
	public static readonly TEST = new TestEntityDescription();
}
